import { differenceInCalendarDays, format, startOfDay, subDays } from "date-fns";
import type { TFunction } from "i18next";
import { rollingDays } from "../components/dashboard/dateSelection";
import type { ChatScopeFilters } from "../api";
import type { ContextualAsk } from "./AskContext";
import type { AskIcon } from "./askIcons";

export type AskSuggestion = {
  label: string;
  icon?: AskIcon;
  prompt?: string;
  detail?: string;
  detailTitle?: string;
  scope?: { filters: ChatScopeFilters; label: string };
};
export type AskAction = "kpi" | "processes" | "explore";

/** Calendar boundaries avoid shifting a comparison by an hour across daylight saving time. */
export function askComparisonPeriods(target: ContextualAsk, now = new Date()) {
  const { from, to } = target.filters;
  const bounded = from && to && new Date(from).getTime() !== 0;
  const end = bounded ? new Date(to) : startOfDay(now);
  const start = bounded ? new Date(from) : subDays(end, 30);
  const days = differenceInCalendarDays(end, start);
  if (!Number.isFinite(days) || days <= 0) return null;
  const previous = subDays(start, days);
  const label = (a: Date, b: Date) =>
    `${format(a, "dd MMM yyyy")} – ${format(new Date(b.getTime() - 1), "dd MMM yyyy")}`;
  return {
    current: label(start, end),
    previous: label(previous, start),
    defaulted: !bounded,
    from: previous.toISOString(),
    to: end.toISOString(),
    days,
    compactCurrent: `${format(start, "d MMM")} – ${format(new Date(end.getTime() - 1), "d MMM")}`,
    compactPrevious: `${format(previous, "d MMM")} – ${format(new Date(start.getTime() - 1), "d MMM")}`,
  };
}

export function askStarters(target: ContextualAsk, t: TFunction, now = new Date()) {
  const page = target.page;
  if (!page) return null;
  const question = (key: Parameters<TFunction>[0], icon: AskIcon): AskSuggestion => ({
    label: t(key) as string,
    icon,
  });
  const trend = (): AskSuggestion => {
    const periods = askComparisonPeriods(target, now);
    const relative = periods && (periods.defaulted || rollingDays(target.datePreset ?? ""));
    const detail = periods
      ? t("askPanel.periods", {
          current: relative
            ? t("dashboard.last_n_days", { count: periods.days })
            : periods.compactCurrent,
          previous: relative
            ? t("dashboard.previous_n_days", { count: periods.days })
            : periods.compactPrevious,
        })
      : t("askPanel.choose_period");
    return {
      label: t("askPanel.changed"),
      icon: "change",
      detail,
      detailTitle: periods
        ? t("askPanel.periods", {
            current: periods.current,
            previous: periods.previous,
          })
        : undefined,
      scope: periods
        ? {
            filters: { ...target.filters, from: periods.from, to: periods.to },
            label: `${target.label} (${detail})`,
          }
        : undefined,
      prompt: periods
        ? t("askPanel.trend_prompt", { current: periods.current, previous: periods.previous })
        : t("askPanel.changed"),
    };
  };
  const result = (questions: AskSuggestion[], action: AskAction, actionLabel: string) => ({
    questions,
    action,
    actionLabel,
    title: t(`askPanel.title_${page.kind}`),
  });
  switch (page.kind) {
    case "overview":
      return result(
        [
          page.insight
            ? {
                label: t("askPanel.insight", { process: page.insight }),
                icon: "share",
                prompt: t("askPanel.insight_prompt", { process: page.insight }),
              }
            : question("askPanel.investigate", "investigate"),
          trend(),
          question("askPanel.opportunity", "opportunity"),
          question("askPanel.automation", "automation"),
        ],
        "kpi",
        t("askPanel.build_team"),
      );
    case "processes":
      return result(
        [
          question("askPanel.investigate", "investigate"),
          question("askPanel.variation", "duration"),
          question("askPanel.complexity", "paths"),
          question("askPanel.rework", "rework"),
        ],
        "processes",
        t("askPanel.compare_processes"),
      );
    case "process":
      return result(
        [
          {
            label: t("askPanel.duration", { process: target.filters.processes?.[0] ?? page.name }),
            icon: "duration",
          },
          question("askPanel.steps", "steps"),
          question("askPanel.rework", "rework"),
          question("askPanel.process_people", "people"),
        ],
        "kpi",
        t("askPanel.build_process"),
      );
    case "team":
      return result(
        [
          question("askPanel.patterns", "patterns"),
          question("askPanel.different_steps", "divergence"),
          question("askPanel.people_variation", "people"),
          trend(),
        ],
        "kpi",
        t("askPanel.build_team"),
      );
    case "session":
      return result(
        [
          question("askPanel.session_summary", "summary"),
          question("askPanel.session_processes", "performed"),
          question("askPanel.session_time", "time"),
          {
            label: t("askPanel.unusual"),
            icon: "unusual",
            detail: t("askPanel.baseline"),
            prompt: t("askPanel.unusual_prompt"),
          },
        ],
        "explore",
        t("askPanel.explore_process"),
      );
    case "kpis":
      return result(
        [
          question("askPanel.kpi_attention", "attention"),
          question("askPanel.kpi_changed", "change"),
          question("askPanel.kpi_drivers", "investigate"),
          question("askPanel.kpi_processes", "share"),
        ],
        "kpi",
        t("askPanel.build_kpi"),
      );
  }
}
